// File: my-spa/src/layouts/AdminLayout.tsx
import React, { useState, useContext } from "react";
import { Outlet, Link, useLocation } from "react-router-dom";
import AdminToolbar from "../components/AdminToolbar";
import { AuthContext, UserRole } from "../auth/authContext";
import { PERMISSIONS } from "../auth/permissions";
import {
  FiHome,
  FiBarChart2,
  FiDollarSign,
  FiMessageSquare,
  FiUsers,
  FiPackage,
  FiScissors,
  FiFileText,
  FiCalendar,
  FiUser,
  FiClipboard,
  FiUserCheck,
  FiSettings,
  FiMenu,
  FiX,
} from "react-icons/fi";

// Crear componentes con tipo any para evitar los errores de TypeScript
const FiXIcon: React.FC<any> = FiX;
const FiMenuIcon: React.FC<any> = FiMenu;
const FiHomeIcon: React.FC<any> = FiHome;
const FiBarChart2Icon: React.FC<any> = FiBarChart2;
const FiDollarSignIcon: React.FC<any> = FiDollarSign;
const FiMessageSquareIcon: React.FC<any> = FiMessageSquare;
const FiUsersIcon: React.FC<any> = FiUsers;
const FiPackageIcon: React.FC<any> = FiPackage;
const FiScissorsIcon: React.FC<any> = FiScissors;
const FiFileTextIcon: React.FC<any> = FiFileText;
const FiCalendarIcon: React.FC<any> = FiCalendar;
const FiUserIcon: React.FC<any> = FiUser;
const FiClipboardIcon: React.FC<any> = FiClipboard;
const FiUserCheckIcon: React.FC<any> = FiUserCheck;
const FiSettingsIcon: React.FC<any> = FiSettings;

interface SidebarItem {
  to: string;
  label: string;
  icon: React.FC<any>;
  visible: boolean;
}

const AdminLayout: React.FC = () => {
  const { hasRole, hasPermission, hasAnyPermission } =
    useContext(AuthContext);
  const location = useLocation();
  const [isSidebarOpen, setSidebarOpen] = useState(false);

  const toggleSidebar = () => {
    setSidebarOpen(!isSidebarOpen);
  };

  const isActiveLink = (path: string): boolean => {
    return location.pathname === path;
  };

  // Cada módulo se muestra según los permisos efectivos del usuario
  const sidebarItems: SidebarItem[] = [
    {
      to: "/admin",
      label: "Dashboard",
      icon: FiHomeIcon,
      visible: hasPermission(PERMISSIONS.METRICS_VIEW),
    },
    {
      to: "/admin/metrics",
      label: "Métricas",
      icon: FiBarChart2Icon,
      visible: hasPermission(PERMISSIONS.METRICS_VIEW),
    },
    {
      to: "/admin/payroll",
      label: "Liquidación Empleados",
      icon: FiDollarSignIcon,
      visible: hasPermission(PERMISSIONS.PAYROLL_VIEW),
    },
    {
      to: "/admin/send-message",
      label: "Enviar Mensaje WhatsApp",
      icon: FiMessageSquareIcon,
      visible: hasPermission(PERMISSIONS.WHATSAPP_SEND),
    },
    {
      to: "/admin/employees",
      label: "Gestionar Empleados",
      icon: FiUsersIcon,
      visible: hasPermission(PERMISSIONS.EMPLOYEES_MANAGE),
    },
    {
      to: "/admin/products",
      label: "Gestionar Productos",
      icon: FiPackageIcon,
      visible: hasAnyPermission(
        PERMISSIONS.PRODUCTS_MANAGE,
        PERMISSIONS.PRODUCT_SALES_CREATE,
        PERMISSIONS.PRODUCT_SALES_VIEW,
      ),
    },
    {
      to: "/admin/treatments",
      label: "Gestionar Tratamientos",
      icon: FiScissorsIcon,
      visible: hasPermission(PERMISSIONS.TREATMENTS_MANAGE),
    },
    {
      to: "/admin/services-packs",
      label: "Gestionar Paquetes",
      icon: FiPackageIcon,
      visible: hasPermission(PERMISSIONS.SERVICES_PACKS_MANAGE),
    },
    {
      to: "/admin/client-packs",
      label: "Paquetes de Clientes",
      icon: FiUsersIcon,
      visible: hasPermission(PERMISSIONS.CLIENT_PACKS_MANAGE),
    },
    {
      to: "/admin/register-pack-session",
      label: "Registrar Sesión",
      icon: FiClipboardIcon,
      visible: hasPermission(PERMISSIONS.CLIENT_PACKS_MANAGE),
    },
    {
      to: "/admin/blogs",
      label: "Gestionar Blogs",
      icon: FiFileTextIcon,
      visible: hasPermission(PERMISSIONS.BLOGS_MANAGE),
    },
    {
      to: "/admin/employee-availabilities",
      label: "Gestionar Disponibilidad",
      icon: FiCalendarIcon,
      visible: hasPermission(PERMISSIONS.EMPLOYEE_AVAILABILITIES_MANAGE),
    },
    {
      to: "/admin/users",
      label: "Gestionar Usuarios",
      icon: FiUserCheckIcon,
      // La gestión de usuarios es exclusiva del administrador
      visible: hasRole(UserRole.ADMIN),
    },
    {
      to: "/admin/appointments",
      label: "Gestionar Citas",
      icon: FiClipboardIcon,
      visible: hasPermission(PERMISSIONS.APPOINTMENTS_VIEW),
    },
    {
      to: "/admin/clients",
      label: "Gestionar Clientes",
      icon: FiUserIcon,
      visible: hasPermission(PERMISSIONS.CLIENTS_VIEW),
    },
    {
      to: "/admin/change-password",
      label: "Cambiar Contraseña",
      icon: FiSettingsIcon,
      visible: true,
    },
  ];

  return (
    <div className="admin-layout">
      <AdminToolbar />

      {/* Botón de hamburguesa para móviles */}
      <button className="admin-menu-toggle" onClick={toggleSidebar}>
        {isSidebarOpen ? <FiXIcon size={24} /> : <FiMenuIcon size={24} />}
      </button>

      {/* Overlay para cerrar el menú al hacer clic fuera */}
      {isSidebarOpen && (
        <div className="sidebar-overlay" onClick={toggleSidebar}></div>
      )}

      <div className="admin-layout-container">
        <nav className={`admin-sidebar ${isSidebarOpen ? "is-open" : ""}`}>
          <div className="sidebar-header">
            <h2>Panel de Administración</h2>
          </div>

          <ul className="admin-sidebar-list">
            {sidebarItems
              .filter((item) => item.visible)
              .map((item) => (
                <li key={item.to}>
                  <Link
                    to={item.to}
                    className={`admin-sidebar-link ${
                      isActiveLink(item.to) ? "active" : ""
                    }`}
                    onClick={toggleSidebar}
                  >
                    <item.icon className="sidebar-icon" />
                    <span>{item.label}</span>
                  </Link>
                </li>
              ))}
          </ul>
        </nav>

        <main className="admin-main-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
